import {useMemo} from "react";
import {Link} from "react-router";
import {Plus, Loader2Icon} from "lucide-react";
import {createSerializer, parseAsString, useQueryState} from "nuqs";
import {List, RowComponentProps} from "react-window";
import {useInfiniteLoader} from "react-window-infinite-loader";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {usePlayerView} from "@/player/PlayerViewHooks.ts";
import {Scene} from "@/scenes/Scene.ts";
import SceneListItem from "@/scenes/SceneListItem.tsx";
import {useScenesQuery} from "@/scenes/SceneQueries.ts";

const PAGE_SIZE = 50;
const searchParsers = {name: parseAsString.withDefault("")};
const serializeSearch = createSerializer(searchParsers);

export default function SceneListPanel({selectedId}: {selectedId?: string}) {
    const [name, setName] = useQueryState("name", searchParsers.name);
    const {campaign, campaignPath} = useCampaign();
    const {display} = usePlayerView();
    const filter = useMemo(() => ({campaignId: campaign.id, name}), [campaign.id, name]);
    const query = useScenesQuery(filter, PAGE_SIZE);
    const search = serializeSearch({name});
    const sceneBasePath = campaignPath("scenes");
    const onRowsRendered = useInfiniteLoader({
        isRowLoaded: index => index < query.scenes.length,
        loadMoreRows: query.loadMore,
        rowCount: query.rowCount,
        minimumBatchSize: PAGE_SIZE,
    });

    return (
        <>
            <div className="p-1"><SearchBar value={name} onChange={setName}/></div>
            <List className="bg-surface"
                  onRowsRendered={onRowsRendered}
                  rowComponent={VirtualSceneRow}
                  rowCount={query.rowCount}
                  rowHeight={40}
                  rowProps={{
                      scenes: query.scenes,
                      selectedId,
                      displayedId: display.display === "scene" ? display.scene.id : undefined,
                      sceneBasePath,
                      search,
                  }}/>
            <div className="flex items-center justify-between gap-2 px-2 py-1">
                <span className="text-sm text-muted-foreground">
                    {query.isError ? "Szenen konnten nicht geladen werden" : `${query.totalCount} Ergebnisse`}
                </span>
                <Button size="sm" asChild>
                    <Link to={{pathname: campaignPath("scenes/new"), search}}>Szene <Plus/></Link>
                </Button>
            </div>
        </>
    );
}

function VirtualSceneRow({index, style, scenes, selectedId, displayedId, sceneBasePath, search}: RowComponentProps<{
    scenes: Scene[];
    selectedId?: string;
    displayedId?: string;
    sceneBasePath: string;
    search: string;
}>) {
    const scene = scenes[index];
    if (!scene) return <div style={style} className="p-3 text-muted-foreground"><Loader2Icon className="animate-spin"/></div>;
    return <SceneListItem scene={scene}
                          href={`${sceneBasePath}/${scene.id}`}
                          search={search}
                          selected={scene.id === selectedId}
                          displayed={scene.id === displayedId}
                          style={style}/>;
}
